import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, MaskedLines } from "../components/Reveal";
import Marquee from "../components/Marquee";
import SEO from "../components/SEO";

const IMG_LASER = "https://images.pexels.com/photos/7254419/pexels-photo-7254419.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1200&w=1000";

function ParallaxImg({ src, className }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-12%", "12%"]);
  return (
    <div ref={ref} className={`overflow-hidden border border-line ${className}`}>
      <motion.img style={{ y, scale: 1.2 }} src={src} alt="Graveuse laser en fonctionnement" className="h-full w-full object-cover" />
    </div>
  );
}

export default function Atelier() {
  return (
    <div>
      <SEO title="L'Atelier — fait main en Suisse" path="/atelier"
        description="o-crea est un atelier tenu par une seule personne : gravure laser sur bois et métal, sublimation sur mugs, gourdes et sacs. Chaque pièce est contrôlée à la main avant l'envoi." />
      <section className="mx-auto max-w-[1400px] px-5 pt-24 pb-12 md:px-10">
        <p className="mb-4 text-xs uppercase tracking-[0.3em] text-terra">L'atelier</p>
        <h1 className="max-w-4xl font-serif text-5xl font-light leading-[0.95] tracking-tight text-ink md:text-8xl">
          <MaskedLines lines={["Une personne.", "Deux techniques."]} />
          <span className="italic text-terra"><MaskedLines lines={["Vos idées."]} delay={0.3} /></span>
        </h1>
      </section>

      <section className="mx-auto grid max-w-[1400px] items-center gap-10 px-5 pb-24 md:grid-cols-12 md:px-10">
        <Reveal delay={0.2} className="md:col-span-6">
          <p className="max-w-xl text-lg leading-relaxed text-ink2">
            o-crea est né dans un coin d'appartement du canton de Vaud, entre une presse à chaud et une graveuse laser.
            Aujourd'hui, deux savoir-faire cohabitent : la <strong className="text-ink">gravure laser</strong> sur bois et métal, et la <strong className="text-ink">sublimation</strong> sur mugs, gourdes et sacs.
            Tout passe entre les mêmes mains, ici en terre vaudoise : le choix des matières, le calage du motif,
            le contrôle de chaque pièce avant l'envoi. Rien n'est délégué — c'est ce qui rend chaque objet un peu vivant.
          </p>
        </Reveal>
        <ParallaxImg src={IMG_LASER} className="aspect-[4/5] md:col-span-6" />
      </section>

      <Marquee items={["Gravure Laser", "Bois & Métal", "Sublimation", "Fait Maison", "Pièce Unique"]} />

      {/* Nos techniques */}
      <section className="mx-auto max-w-[1400px] px-5 pb-24 md:px-10">
        <Reveal>
          <p className="mb-4 text-xs uppercase tracking-[0.3em] text-terra">Ce qu&apos;on fait</p>
          <h2 className="max-w-2xl font-serif text-4xl font-light leading-tight tracking-tight text-ink md:text-6xl">
            Deux techniques, deux univers.
          </h2>
          <p className="mt-4 max-w-2xl leading-relaxed text-ink2">
            Selon l&apos;objet et le rendu que vous cherchez, on grave ou on sublime. Découvrez chaque technique et comment ça se passe.
          </p>
        </Reveal>
        <div className="mt-12 grid gap-6 md:grid-cols-2">
          <Reveal>
            <Link
              to="/gravure-laser" data-testid="atelier-gravure-card"
              className="group flex h-full flex-col justify-between border border-line bg-sand/40 p-8 transition-colors hover:bg-sand md:p-10"
            >
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-terra">Bois & métal</p>
                <h3 className="mt-3 font-serif text-3xl font-medium text-ink md:text-4xl">Gravure laser</h3>
                <p className="mt-3 max-w-md leading-relaxed text-ink2">
                  Une marque permanente, gravée dans la matière. Monochrome, nette, durable — idéale pour un cadeau qui dure.
                </p>
              </div>
              <span className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-widest text-ink">
                Comment ça marche
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </span>
            </Link>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/sublimation" data-testid="atelier-sublimation-card"
              className="group flex h-full flex-col justify-between border border-line bg-sand/40 p-8 transition-colors hover:bg-sand md:p-10"
            >
              <div>
                <p className="text-xs uppercase tracking-[0.3em] text-terra">Mugs, gourdes, textile…</p>
                <h3 className="mt-3 font-serif text-3xl font-medium text-ink md:text-4xl">Sublimation</h3>
                <p className="mt-3 max-w-md leading-relaxed text-ink2">
                  La couleur fondue dans la matière : photos, dégradés, motifs éclatants qui ne s&apos;écaillent pas.
                </p>
              </div>
              <span className="mt-8 inline-flex items-center gap-2 text-sm uppercase tracking-widest text-ink">
                Comment ça marche
                <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
              </span>
            </Link>
          </Reveal>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 pb-32 md:px-10">
        <Reveal className="border border-line bg-ink p-10 text-cream md:p-20">
          <h2 className="max-w-2xl font-serif text-4xl font-light leading-tight tracking-tight md:text-6xl">
            Prêt à créer votre pièce ?
          </h2>
          <Link
            to="/catalogue" data-testid="atelier-cta"
            className="mt-8 inline-block border border-cream px-8 py-4 text-sm uppercase tracking-widest transition-colors hover:bg-cream hover:text-ink active:scale-95"
          >
            Ouvrir le catalogue
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
